
import { Box, useMediaQuery } from "@mui/material";
import { color } from "chart.js/helpers";
import { defaults } from "chart.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { useTheme } from "@emotion/react";
import ZoomControls from "./ZoomControls";
import { formatNumber, formatNumberWithLocale , formatAxisValue} from "../../utils/charts";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    zoomPlugin
);


const groupedLabelsPlugin = (theme, categories) => {
    return {
        id: 'groupedLabels',
        afterDraw: (chart) => {
            const ctx = chart.ctx;
            const xAxis = chart.scales['x'];
            const yAxis = chart.scales['y'];

            const years = chart.config.data.labels; // Now just the areas

            if (categories.length <= 1) {
                return; // don't render grouped categories if there's only one area
            }

            const { left, right } = xAxis;
            const totalLabels = chart.config.data.labels.length;
            const stepSize = (right - left) / totalLabels;

            ctx.save();
            ctx.textAlign = 'left';
            ctx.font = '10px Arial';
            ctx.fillStyle = theme.palette.primary.fontColor;

            let currentArea = null;
            let areaStartIndex = 0;

            years.forEach((area, i) => {
                if (area !== currentArea) {
                    if (currentArea !== null) {
                        const groupStart = left + (areaStartIndex * stepSize);
                        const groupEnd = left + ((i) * stepSize);
                        const groupCenter = (groupStart + groupEnd) / 2;

                        ctx.save();
                        ctx.translate(groupCenter, yAxis.bottom + 65);
                        ctx.rotate(-Math.PI / 2);
                        ctx.fillText(currentArea, 0, 0);
                        ctx.restore();

                        // Draw separator for each group
                        ctx.beginPath();
                        ctx.moveTo(groupEnd, yAxis.bottom + 100);
                        ctx.lineTo(groupEnd, yAxis.bottom + 0);
                        ctx.strokeStyle = '#ccc';
                        ctx.stroke();
                    }

                    currentArea = area;
                    areaStartIndex = i;
                }
            });

            // Draw the last area's label
            const lastGroupStart = left + (areaStartIndex * stepSize);
            const lastGroupEnd = right;
            const lastGroupCenter = (lastGroupStart + lastGroupEnd) / 2;

            ctx.save();
            ctx.translate(lastGroupCenter, yAxis.bottom + 65);
            ctx.rotate(-Math.PI / 2);
            ctx.fillText(currentArea, 0, 0);
            ctx.restore();

            ctx.restore();
        },
    };
};

export default function BarChartStacked({ data, showDataLabels, hideSubregions, title }) {
    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);

    // Create a structured data format
    const structuredData = {
        labels: data.labels.map((label) => label[0]), // extract years from labels
        datasets: data.datasets.map((dataset) => {
            const isLine =
                dataset.label === "All food groups" ||
                dataset.label === "Meat, Total" ||
                dataset.label === "Livestock" ||
                dataset.label === "Crops";
            const isFewBars = data.labels.length <= 3;

            return {
                label: dataset.label,
                data: dataset.data.map((value) => parseFloat(value)),
                flagDescriptions: dataset.flagDescriptions,
                backgroundColor: dataset.backgroundColor,
                borderColor: dataset.borderColor,
                type: isLine ? 'line' : 'bar',
                tension: isLine ? 0.1 : 0,
                yAxisID: isLine ? 'y1' : 'y',
                fill: false,
                stack: dataset.stack,
                // Stacked bar charts always show a single country (see useGroupedItems.js),
                // so there are no subregions to hide here - each dataset is an item category.
                hidden: false,
                order: isLine ? 0 : 1,
                barPercentage: isLine ? undefined : (isFewBars ? 0.5 : 1),
                categoryPercentage: isLine ? undefined : (isFewBars ? 0.6 : 0.8),
            };
        }),
    };

    const categories = [...new Set(data.labels.map((label) => label[1]))]; // Extract unique area names



    const barDatasets = structuredData.datasets.filter(ds => ds.type !== 'line' && ds.hidden !== true);
    const stackedSumsPerIndex = structuredData.labels.map((_, i) =>
        barDatasets.reduce((sum, ds) => sum + (Number(ds.data[i]) || 0), 0)
    );
    // Index (within structuredData.datasets) of the topmost non-empty bar segment per label,
    // i.e. the last dataset in stacking order that has a non-zero value at that index -
    // that's the only segment that should carry the "total" datalabel above the bar.
    const topBarDatasetIndexPerLabel = structuredData.labels.map((_, i) => {
        let top = -1;
        structuredData.datasets.forEach((ds, dsIndex) => {
            if (ds.type !== 'line' && ds.hidden !== true && Number(ds.data[i])) {
                top = dsIndex;
            }
        });
        return top;
    });
    // The line series lives on y1, but both axes share this max so the two stay comparable.
    // Sizing it on the bars alone clipped any line running above the tallest stack.
    const lineValues = structuredData.datasets
        .filter(ds => ds.type === 'line' && ds.hidden !== true)
        .flatMap(ds => ds.data.map(point => Number(point?.y ?? point) || 0));
    const maxYValue = Math.max(...stackedSumsPerIndex, ...lineValues);
    const augmentedMaxYValue =
        maxYValue < 2 ? Math.round(maxYValue * 1.5) : Math.round(maxYValue * 1.1);


    const options = {
        layout: {
            padding: {
                top: categories.length > 1 ? 0 : 30,
                right: isMobile ? 10 : 25,
                bottom: categories.length > 1 ? 80 : 20,
                left: isMobile ? 5 : 15,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index",
            intersect: false,
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                },
            },
            y: {
                stacked: true,
                grid: {
                    color: theme.palette.mode === "dark" ? "#333" : "#e7e7e7",
                },
                title: {
                    display: true,
                    text: data.info.unit,
                    color: theme.palette.primary.fontColor,
                    padding: 0,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                    padding: 10,
                    min: 0,
                    // Zooming or panning leaves the axis ending wherever the drag stopped, and Chart.js labels
                    // that end too - 23.7 above 20, 48.3 above 45. Only the regular steps are labelled.
                    includeBounds: false,
                    callback: function (value, index, ticks) {
                        // Shared with the other charts: decimals come from the tick step, so a series
                        // sitting near zero no longer prints the same label three times.
                        return formatAxisValue(value, ticks);
                    },
                },
                max: augmentedMaxYValue,
            },
            y1: {
                // separate y-axis for the line chart
                position: "right",
                grid: { drawOnChartArea: false },
                title: { display: false },
                display: false,
                max: augmentedMaxYValue,
            },
        },
        animation: {
            duration: 0,
        },
        plugins: {
            legend: {
                position: categories.length > 1 ? "top" : "bottom", // Show at top if more than 1 area
                labels: {
                    padding: isMobile ? 8 : 12,
                    usePointStyle: true,
                    color: theme.palette.primary.fontColor,
                    generateLabels: function (chart) {
                        const original = defaults.plugins.legend.labels.generateLabels;
                        const labelsOriginal = original.call(this, chart);
                        const maxLength = 95; // max length of the legend text

                        return labelsOriginal
                            .map((label) => {
                                if (label.hidden && hideSubregions) {
                                    return null;
                                } else if (label.hidden && !hideSubregions) {
                                    label.fontColor =
                                        theme.palette.mode === "light"
                                            ? color(label.fontColor).lighten(2).rgbString()
                                            : "#666";
                                    label.fillStyle =
                                        theme.palette.mode === "light"
                                            ? color(label.fillStyle).lighten(0.5).rgbString()
                                            : "#666";
                                    label.strokeStyle =
                                        theme.palette.mode === "light"
                                            ? color(label.strokeStyle).lighten(0.5).rgbString()
                                            : "#666";
                                } else {
                                    label.fontColor = theme.palette.primary.fontColor;
                                    //   label.fillStyle = label.fillStyle;
                                    //   label.strokeStyle = label.strokeStyle;
                                }

                                // ensure that all items are shown in the legend
                                label.hidden = false;
                                if (label.text.length > maxLength) {
                                    label.text = label.text.substring(0, maxLength) + "...";
                                }
                                return label;
                            })
                            .filter((label) => label !== null);
                    },
                },
            },
            groupedLabels: groupedLabelsPlugin(theme), // Use the updated grouped labels plugin
            datalabels: {
                color: theme.palette.primary.fontColor,
                align: "end",
                anchor: "end",
                clamp: true,
                display: (context) => {
                    if (!showDataLabels) return false;
                    // Bar segments: only the topmost non-empty segment of the stack shows a
                    // label (the stack's true total), instead of every segment showing its
                    // own value at the "end" anchor - which used to look like a wrong total.
                    if (context.dataset.type === 'line') return "auto";
                    return context.datasetIndex === topBarDatasetIndexPerLabel[context.dataIndex] ? "auto" : false;
                },
                backgroundColor: "rgba(255, 255, 255, 0)",
                font: {
                    size: 10,
                },
                padding: 4,
                formatter: (value, context) => {
                    if (context.dataset.type === 'line') return formatNumber(value);
                    return formatNumber(stackedSumsPerIndex[context.dataIndex]);
                },
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || "";
                        const value = formatNumberWithLocale(context.parsed?.y);
                        const unit = data.info.unit || "";
                        return `${label}: ${value} ${unit}`;
                    },
                    labelTextColor: function () {
                        return "#000";
                    },
                    footer: function (context) {
                        // Chart.js hands this an empty array when the tooltip fires with no
                        // items (data swapped mid-hover, every item filtered out), which used
                        // to crash on context[0].
                        const item = context?.[0];
                        if (!item) return "";
                        const idx = item.dataIndex;
                        const dsIdx = item.datasetIndex;
                        const pointFlag = structuredData.datasets[dsIdx]?.flagDescriptions?.[idx] || "";
                        const flagDescription = pointFlag || data.info.flag_description || "";
                        return flagDescription ? `${flagDescription}` : "";
                    },
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                footerColor: "#333",
                footerFont: { weight: "normal" },
                borderColor: "#ccc",
                borderWidth: "1",
            },
            title: {
                display: false,
                text: title,
                font: {
                    size: 14,
                    lineHeight: 1.2,
                },
                padding: { top: 0, left: 0, right: 0, bottom: 30 },
            },
            zoom: {
                limits: {
                    x: {
                        min: 'original',
                        max: 'original',
                        minRange: 2
                    },
                    y: {
                        min: 'original',
                        max: 'original',
                        minRange: 0.1
                    },
                    y1: {
                        min: 'original',
                        max: 'original',
                        minRange: 0.1
                    }
                },
                zoom: {
                    wheel: {
                        enabled: false,
                    },
                    pinch: {
                        enabled: false
                    },
                    drag: {
                        enabled: true,
                        backgroundColor: 'rgba(225,225,225,0.3)',
                        borderColor: 'rgb(225,225,225)',
                        borderWidth: 1,
                        threshold: 100,
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'xy',
                    rangeMin: {
                        x: null,
                        y: null,
                        y1: null
                    },
                    rangeMax: {
                        x: null,
                        y: null,
                        y1: null
                    }
                }
            },
        },
    };

    return (
        <Box className="max-chart-height">
            <Bar
                options={options}
                data={structuredData}
                plugins={[groupedLabelsPlugin(theme, categories)]}
                ref={ref}
            />
            <ZoomControls chartRef={ref} data={structuredData} />
        </Box>
    );
}